/**
 * Harmonised colour palettes for procedural houses.
 *
 * Every house colour is a pure function of its seed, so a street keeps its
 * paint across eviction and rebuild exactly the way the tree scatter keeps its
 * layout. Nothing here is random at runtime.
 *
 * Colours are authored in HSL rather than as hex tables because the palette has
 * to *harmonise*, not just vary: a roof is chosen from a curated set and then
 * pulled part of the way toward the wall's hue, which is what stops a mint
 * house from getting a clay roof that fights it. Working in hex would make that
 * nudge unexpressible.
 *
 * Output is `Uint8Array` triples in **linear light**, not sRGB — that is the
 * space a Three vertex-colour attribute is sampled in, and converting once here
 * is cheaper than converting per vertex later. `housePaletteSrgb` exposes the
 * unconverted bytes for tests and for tools that want to show swatches.
 */

/** Palette slots. A vertex carries a slot index; the instance carries colours. */
export const SLOT_WALL = 0;
/** Gables, upper storeys and dormer cheeks — a shade off the main wall. */
export const SLOT_WALL_ALT = 1;
/** Window frames, corner boards, fascia trim, door surrounds. */
export const SLOT_TRIM = 2;
export const SLOT_ROOF = 3;
/** The roof's own edge and underside, lifted so the slab reads as solid. */
export const SLOT_ROOF_EDGE = 4;
export const SLOT_GLASS = 5;
/** A window with a light on, used for a deterministic minority of openings. */
export const SLOT_GLASS_LIT = 6;
export const SLOT_DOOR = 7;
export const SLOT_CHIMNEY = 8;
/** Window shutters and flower boxes — the house's accent colour. */
export const SLOT_SHUTTER = 9;
export const SLOT_FLOWER = 10;
export const SLOT_COUNT = 11;

/**
 * The building tradition of a place, chosen from its biome. The same footprint
 * is a painted timber house in Sweden, a flat-roofed adobe block in the Sahara
 * and a bright red cottage with a snow-covered roof in Greenland.
 */
export type HouseRegion = 'temperate' | 'arid' | 'mediterranean' | 'tropical' | 'arctic';

/** A palette as `SLOT_COUNT` RGB triples. */
export type HousePalette = Uint8Array;

interface Hsl {
  /** Degrees, 0..360. */
  h: number;
  s: number;
  l: number;
}

/**
 * Wall tones.
 *
 * Two families on purpose: pale renders that read as diorama pastel, and the
 * saturated dark reds and ochres of the Nordic countryside this world is
 * streamed from. Mixing them is what keeps a village from looking like one
 * house cloned twenty times, and the dark ones are where the white trim earns
 * its place.
 */
const WALL_TONES: Hsl[] = [
  // Weighted by repetition toward what the diorama reference is full of:
  // white render under slate, cream, and bright barn red with white trim.
  { h: 220, s: 0.12, l: 0.93 }, // white render
  { h: 220, s: 0.12, l: 0.93 },
  { h: 40, s: 0.30, l: 0.92 },  // off-white
  { h: 40, s: 0.52, l: 0.85 },  // warm cream
  { h: 40, s: 0.52, l: 0.85 },
  { h: 356, s: 0.62, l: 0.47 }, // barn red
  { h: 356, s: 0.62, l: 0.47 },
  { h: 28, s: 0.44, l: 0.80 },  // sand
  { h: 16, s: 0.40, l: 0.79 },  // pale terracotta
  { h: 6, s: 0.36, l: 0.83 },   // blush
  { h: 96, s: 0.28, l: 0.79 },  // pale sage
  { h: 152, s: 0.24, l: 0.79 }, // mint grey
  { h: 200, s: 0.30, l: 0.81 }, // pale sky
  { h: 222, s: 0.20, l: 0.78 }, // cool grey
  { h: 0, s: 0.00, l: 0.91 },   // white
  { h: 44, s: 0.54, l: 0.68 },  // mustard
  { h: 8, s: 0.50, l: 0.40 },   // falu red
  { h: 14, s: 0.44, l: 0.46 },  // oxide red
  { h: 40, s: 0.52, l: 0.56 },  // ochre
  { h: 142, s: 0.22, l: 0.36 }, // bottle green
  { h: 212, s: 0.20, l: 0.44 }, // slate blue
];

/** Roof tones, before harmonisation. Deliberately all darker than any wall. */
const ROOF_TONES: Hsl[] = [
  // Mid-tones, as in the reference renders — a roof is the sunlit top of a
  // model there, not a dark lid — and slate blue weighted up because that is
  // the roof the reference keeps returning to.
  { h: 212, s: 0.30, l: 0.50 }, // slate blue
  { h: 212, s: 0.30, l: 0.50 },
  { h: 214, s: 0.22, l: 0.42 }, // deep slate
  { h: 12, s: 0.52, l: 0.47 },  // clay tile
  { h: 22, s: 0.36, l: 0.42 },  // weathered shingle
  { h: 6, s: 0.40, l: 0.38 },   // dark brick
  { h: 34, s: 0.28, l: 0.40 },  // brown shingle
  { h: 200, s: 0.10, l: 0.36 }, // zinc
  { h: 120, s: 0.20, l: 0.40 }, // moss
];

/** Shutters for pale walls: the dark greens, blues and reds of the reference. */
const SHUTTER_TONES: Hsl[] = [
  { h: 158, s: 0.55, l: 0.29 }, // shutter green
  { h: 158, s: 0.55, l: 0.29 },
  { h: 208, s: 0.45, l: 0.36 }, // shutter blue
  { h: 356, s: 0.58, l: 0.40 }, // shutter red
  { h: 40, s: 0.20, l: 0.25 },  // stained timber
];

const FLOWER_TONES: Hsl[] = [
  { h: 342, s: 0.72, l: 0.62 }, // geranium pink
  { h: 356, s: 0.78, l: 0.52 }, // red
  { h: 44, s: 0.9, l: 0.58 },   // marigold
  { h: 280, s: 0.45, l: 0.62 }, // lavender
];

/** Door accents. Picked against the wall hue, not from it. */
const DOOR_TONES: Hsl[] = [
  { h: 4, s: 0.52, l: 0.36 },
  { h: 148, s: 0.40, l: 0.30 },
  { h: 206, s: 0.44, l: 0.34 },
  { h: 32, s: 0.50, l: 0.38 },
  { h: 0, s: 0.00, l: 0.24 },
];

/**
 * A 32-bit mix used to pull independent choices out of one seed.
 *
 * `makeRng` would also work, but every choice here wants a *stable* bit of the
 * seed rather than a position in a stream: adding a colour decision later must
 * not reshuffle the ones already made, or every house in the world repaints.
 */
export function mixSeed(seed: number, salt: number): number {
  let h = Math.imul(seed ^ 0x9e3779b9, 0x85ebca6b) >>> 0;
  h = (h ^ (h >>> 13)) >>> 0;
  h = Math.imul(h ^ salt, 0xc2b2ae35) >>> 0;
  return (h ^ (h >>> 16)) >>> 0;
}

/** A fraction in [0,1) from a seed and salt. */
export function mixUnit(seed: number, salt: number): number {
  return mixSeed(seed, salt) / 4294967296;
}

function pick<T>(items: T[], seed: number, salt: number): T {
  return items[mixSeed(seed, salt) % items.length];
}

/** Shortest-arc interpolation between two hues, in degrees. */
function mixHue(from: number, to: number, t: number): number {
  const delta = ((to - from + 540) % 360) - 180;
  return (from + delta * t + 360) % 360;
}

function clamp01(value: number): number {
  return value < 0 ? 0 : value > 1 ? 1 : value;
}

/** Standard HSL to RGB, returning floats in 0..1. */
function hslToRgb({ h, s, l }: Hsl): [number, number, number] {
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const hp = (((h % 360) + 360) % 360) / 60;
  const x = c * (1 - Math.abs((hp % 2) - 1));
  const m = l - c / 2;
  const table: Array<[number, number, number]> = [
    [c, x, 0], [x, c, 0], [0, c, x], [0, x, c], [x, 0, c], [c, 0, x],
  ];
  const [r, g, b] = table[Math.floor(hp) % 6];
  return [clamp01(r + m), clamp01(g + m), clamp01(b + m)];
}

/** The sRGB transfer function, inverted. */
function srgbToLinear(value: number): number {
  return value <= 0.04045 ? value / 12.92 : Math.pow((value + 0.055) / 1.055, 2.4);
}

/** 256-entry inverse-transfer lookup, so packing a palette costs no `pow`. */
const LINEAR_BYTE = new Uint8Array(256);
for (let i = 0; i < 256; i++) {
  LINEAR_BYTE[i] = Math.round(srgbToLinear(i / 255) * 255);
}

/** Convert an sRGB byte to a linear-light byte. */
export function linearByte(srgb: number): number {
  return LINEAR_BYTE[srgb < 0 ? 0 : srgb > 255 ? 255 : srgb | 0];
}

function shift(tone: Hsl, dl: number, ds = 1): Hsl {
  return { h: tone.h, s: clamp01(tone.s * ds), l: clamp01(tone.l + dl) };
}

/**
 * Civic tones, for the big irregular blocks that stay extruded shells: schools,
 * halls, apartment blocks. Pale render under slate or zinc, like the concrete
 * and stone blocks in the reference — a warehouse painted barn red and roofed
 * in clay dominates a whole town from the air.
 */
const CIVIC_WALLS: Hsl[] = [
  { h: 220, s: 0.10, l: 0.9 },
  { h: 38, s: 0.25, l: 0.86 },
  { h: 30, s: 0.18, l: 0.8 },
  { h: 205, s: 0.12, l: 0.78 },
  { h: 18, s: 0.3, l: 0.74 },
];
const CIVIC_ROOFS: Hsl[] = [
  { h: 212, s: 0.22, l: 0.46 },
  { h: 200, s: 0.08, l: 0.42 },
  { h: 214, s: 0.16, l: 0.36 },
  { h: 30, s: 0.1, l: 0.4 },
];

/** Regional traditions: wall and roof tones that override the defaults. */
const REGIONAL: Record<Exclude<HouseRegion, 'temperate'>, { walls: Hsl[]; roofs: Hsl[]; shutters: Hsl[] }> = {
  // Mud brick, sand render and limewash; roofs are flat and the colour of the
  // walls, doors and shutters the blues and greens of the Maghreb.
  arid: {
    walls: [
      { h: 34, s: 0.45, l: 0.72 }, { h: 28, s: 0.42, l: 0.62 }, { h: 38, s: 0.3, l: 0.86 },
      { h: 40, s: 0.55, l: 0.66 }, { h: 18, s: 0.4, l: 0.68 }, { h: 34, s: 0.45, l: 0.72 },
    ],
    roofs: [{ h: 34, s: 0.35, l: 0.66 }, { h: 30, s: 0.3, l: 0.58 }],
    shutters: [{ h: 205, s: 0.6, l: 0.42 }, { h: 170, s: 0.45, l: 0.35 }, { h: 30, s: 0.4, l: 0.3 }],
  },
  // Whitewash under terracotta.
  mediterranean: {
    walls: [
      { h: 40, s: 0.2, l: 0.95 }, { h: 40, s: 0.2, l: 0.95 }, { h: 42, s: 0.5, l: 0.84 },
      { h: 24, s: 0.5, l: 0.78 }, { h: 12, s: 0.45, l: 0.76 },
    ],
    roofs: [{ h: 16, s: 0.6, l: 0.5 }, { h: 12, s: 0.55, l: 0.45 }, { h: 20, s: 0.55, l: 0.55 }],
    shutters: [{ h: 150, s: 0.45, l: 0.33 }, { h: 205, s: 0.5, l: 0.4 }],
  },
  // Pastels under corrugated metal or red tile.
  tropical: {
    walls: [
      { h: 160, s: 0.45, l: 0.78 }, { h: 340, s: 0.55, l: 0.8 }, { h: 200, s: 0.55, l: 0.8 },
      { h: 50, s: 0.7, l: 0.74 }, { h: 30, s: 0.2, l: 0.93 }, { h: 20, s: 0.65, l: 0.72 },
    ],
    roofs: [{ h: 200, s: 0.08, l: 0.62 }, { h: 8, s: 0.55, l: 0.45 }, { h: 180, s: 0.35, l: 0.42 }],
    shutters: [{ h: 30, s: 0.1, l: 0.95 }, { h: 170, s: 0.5, l: 0.35 }],
  },
  // The saturated primaries of Greenland and the Faroes, trimmed in white.
  arctic: {
    walls: [
      { h: 2, s: 0.7, l: 0.47 }, { h: 44, s: 0.85, l: 0.55 }, { h: 210, s: 0.6, l: 0.45 },
      { h: 150, s: 0.45, l: 0.38 }, { h: 2, s: 0.7, l: 0.47 }, { h: 190, s: 0.35, l: 0.6 },
    ],
    roofs: [{ h: 220, s: 0.08, l: 0.22 }, { h: 0, s: 0, l: 0.25 }],
    shutters: [{ h: 0, s: 0, l: 0.95 }],
  },
};

/**
 * Palette families by what a building is for, from its OSM tags. Houses keep
 * the diorama tones above; the others each have the colours of their kind.
 */
export type PaletteFamily = 'house' | 'civic' | 'block' | 'industrial' | 'church' | 'barn';

const FAMILIES: Record<Exclude<PaletteFamily, 'house' | 'civic'>, { walls: Hsl[]; roofs: Hsl[] }> = {
  // Apartment blocks and town-centre shops: ochre and cream render, brick,
  // pale grey — the lamellhus and the 1930s block, not the painted cottage.
  block: {
    walls: [
      { h: 44, s: 0.5, l: 0.72 }, { h: 12, s: 0.42, l: 0.42 }, { h: 18, s: 0.38, l: 0.52 },
      { h: 40, s: 0.15, l: 0.9 }, { h: 210, s: 0.08, l: 0.78 }, { h: 18, s: 0.45, l: 0.72 },
      { h: 36, s: 0.3, l: 0.8 }, { h: 110, s: 0.16, l: 0.74 }, { h: 44, s: 0.5, l: 0.72 },
    ],
    roofs: [
      { h: 210, s: 0.08, l: 0.3 }, { h: 212, s: 0.22, l: 0.4 }, { h: 12, s: 0.45, l: 0.42 },
      { h: 0, s: 0, l: 0.24 },
    ],
  },
  // Sheet-metal halls: grey, blue-grey, green and oxide panels.
  industrial: {
    walls: [
      { h: 210, s: 0.06, l: 0.72 }, { h: 200, s: 0.18, l: 0.55 }, { h: 140, s: 0.14, l: 0.5 },
      { h: 36, s: 0.18, l: 0.72 }, { h: 0, s: 0, l: 0.84 }, { h: 8, s: 0.45, l: 0.4 },
    ],
    roofs: [{ h: 210, s: 0.05, l: 0.5 }, { h: 200, s: 0.06, l: 0.38 }, { h: 0, s: 0, l: 0.3 }],
  },
  // Limewash and stone under lead, copper patina or tile.
  church: {
    walls: [
      { h: 40, s: 0.2, l: 0.93 }, { h: 40, s: 0.2, l: 0.93 }, { h: 38, s: 0.18, l: 0.8 },
      { h: 12, s: 0.4, l: 0.45 }, { h: 30, s: 0.1, l: 0.64 },
    ],
    roofs: [{ h: 200, s: 0.06, l: 0.32 }, { h: 165, s: 0.3, l: 0.5 }, { h: 12, s: 0.45, l: 0.4 }],
  },
  // Barns, sheds and garages: falu red first, then weathered timber.
  barn: {
    walls: [
      { h: 8, s: 0.55, l: 0.38 }, { h: 8, s: 0.55, l: 0.38 }, { h: 8, s: 0.55, l: 0.38 },
      { h: 30, s: 0.08, l: 0.52 }, { h: 30, s: 0.3, l: 0.34 }, { h: 40, s: 0.3, l: 0.85 },
    ],
    roofs: [{ h: 0, s: 0, l: 0.25 }, { h: 8, s: 0.4, l: 0.35 }, { h: 210, s: 0.05, l: 0.45 }],
  },
};

/** Mapped colours that override the chosen tones, 0xRRGGBB sRGB. */
export interface PaletteOverrides {
  wall?: number;
  roof?: number;
}

function hexToHsl(hex: number): Hsl {
  const r = ((hex >> 16) & 255) / 255;
  const g = ((hex >> 8) & 255) / 255;
  const b = (hex & 255) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  if (max === min) return { h: 0, s: 0, l };
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  const h = max === r ? ((g - b) / d + (g < b ? 6 : 0)) : max === g ? (b - r) / d + 2 : (r - g) / d + 4;
  return { h: h * 60, s, l };
}

/** The tones of one building, still in sRGB. */
function houseTones(
  seed: number, civic = false, region: HouseRegion = 'temperate', snowyRoof = false,
  family: PaletteFamily = civic ? 'civic' : 'house', overrides: PaletteOverrides = {},
): Hsl[] {
  // Churches and industry look like themselves anywhere; homes and blocks
  // follow the local tradition.
  const own = family === 'industrial' || family === 'church';
  const regional = region === 'temperate' || family === 'civic' || own ? undefined : REGIONAL[region];
  const table = family === 'house' || family === 'civic' ? undefined : FAMILIES[family];
  const wall = overrides.wall !== undefined
    ? hexToHsl(overrides.wall)
    : pick(regional?.walls ?? table?.walls ?? (family === 'civic' ? CIVIC_WALLS : WALL_TONES), seed, 0x1f);
  const roofBase = overrides.roof !== undefined
    ? hexToHsl(overrides.roof)
    : pick(regional?.roofs ?? table?.roofs ?? (family === 'civic' ? CIVIC_ROOFS : ROOF_TONES), seed, 0x2b);

  // Pull the roof a third of the way to the wall's hue. Enough to read as
  // deliberate, not enough to lose the roof's own colour identity. A white or
  // grey wall has no hue worth pulling toward — its `h` is an accident of
  // authoring — so near-neutral walls leave the roof alone, or every slate
  // roof over white render turns purple.
  // Nor do hues on opposite sides of the wheel: the shortest arc from clay to
  // slate runs through magenta, and a harmonised roof must never be a colour
  // neither parent had.
  const hueGap = Math.abs(((wall.h - roofBase.h + 540) % 360) - 180);
  const pull = wall.s < 0.2 || hueGap > 80 || overrides.roof !== undefined ? 0 : 0.34;
  const roof: Hsl = {
    h: mixHue(roofBase.h, wall.h, pull),
    s: overrides.roof !== undefined ? roofBase.s : clamp01(roofBase.s * 0.82 + wall.s * 0.18),
    l: roofBase.l,
  };

  // Dark walls take near-white trim, pale walls take a lifted tint of
  // themselves. Trimming a falu-red house in pale pink would be wrong in a way
  // no amount of variation excuses.
  const trim: Hsl = wall.l < 0.58
    ? { h: wall.h, s: 0.06, l: 0.93 }
    : shift(wall, 0.09, 0.42);

  const doorBase = pick(DOOR_TONES, seed, 0x3d);
  const door: Hsl = { h: mixHue(doorBase.h, wall.h, 0.2), s: doorBase.s, l: doorBase.l };

  // A chimney is masonry, not cladding: brick on pale walls, and the wall's own
  // darker tone on the saturated ones, where painted brick is the local habit.
  const chimney: Hsl = wall.l < 0.58
    ? shift(wall, -0.07)
    : { h: mixHue(12, wall.h, 0.25), s: 0.34, l: 0.44 };

  const tones = new Array<Hsl>(SLOT_COUNT);
  tones[SLOT_WALL] = wall;
  // Gables a touch deeper than the wall. Reads as a second plane rather than a
  // second colour, which is what keeps the house looking like one object.
  tones[SLOT_WALL_ALT] = shift(wall, wall.l > 0.5 ? -0.05 : 0.05);
  tones[SLOT_TRIM] = trim;
  tones[SLOT_ROOF] = roof;
  tones[SLOT_ROOF_EDGE] = shift(roof, 0.12, 0.85);
  // Glass is a reflection of a pale sky, not a void. Authored dark enough to
  // read as an opening and light enough that a window does not turn into a
  // black dot as soon as the frame around it falls below a pixel.
  tones[SLOT_GLASS] = { h: 203, s: 0.30, l: 0.31 };
  tones[SLOT_GLASS_LIT] = { h: 44, s: 0.62, l: 0.64 };
  tones[SLOT_DOOR] = door;
  tones[SLOT_CHIMNEY] = chimney;
  // Pale walls take a dark accent; dark walls take white shutters, as a barn
  // takes white doors.
  tones[SLOT_SHUTTER] = regional !== undefined
    ? pick(regional.shutters, seed, 0x4f)
    : wall.l > 0.6 ? pick(SHUTTER_TONES, seed, 0x4f) : { h: wall.h, s: 0.08, l: 0.92 };
  if (snowyRoof) {
    // Snow on the slopes; the fascia and underside stay the roof's own colour,
    // which is what makes a snowy roof read as a roof with snow on it.
    tones[SLOT_ROOF] = { h: 210, s: 0.2, l: 0.95 };
  }
  tones[SLOT_FLOWER] = pick(FLOWER_TONES, seed, 0x5b);
  return tones;
}

/**
 * The palette for one house, as sRGB bytes. `civic` selects the block palette,
 * `region` the building tradition, `snowyRoof` covers the roof in snow.
 */
export function housePaletteSrgb(
  seed: number, civic = false, region: HouseRegion = 'temperate', snowyRoof = false,
  family?: PaletteFamily, overrides?: PaletteOverrides,
): HousePalette {
  const tones = houseTones(seed, civic, region, snowyRoof, family ?? (civic ? 'civic' : 'house'), overrides);
  const out = new Uint8Array(SLOT_COUNT * 3);
  for (let slot = 0; slot < SLOT_COUNT; slot++) {
    const [r, g, b] = hslToRgb(tones[slot]);
    out[slot * 3] = Math.round(r * 255);
    out[slot * 3 + 1] = Math.round(g * 255);
    out[slot * 3 + 2] = Math.round(b * 255);
  }
  return out;
}

/**
 * The palette for one house, as linear-light bytes ready for a vertex-colour
 * attribute.
 */
export function housePalette(seed: number): HousePalette {
  const srgb = housePaletteSrgb(seed);
  const out = new Uint8Array(srgb.length);
  for (let i = 0; i < srgb.length; i++) out[i] = LINEAR_BYTE[srgb[i]];
  return out;
}
